import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, LogOut, Target, Layers, FlaskConical, ChevronDown, Check, Globe } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { getLocalPrefs, savePrefs } from "@/lib/prefs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DIETS = ["halal", "vegetarian", "vegan", "lactose"];
const NOVA_GROUPS = [{ g: 1, c: "#16A34A" }, { g: 2, c: "#84CC16" }, { g: 3, c: "#F59E0B" }, { g: 4, c: "#DC2626" }];
const SCORE_COMPONENTS = ["C1", "C2", "C3"];
const GRADE_TIERS = [
  { key: "excellent", range: "75–100", c: "#16A34A" },
  { key: "good", range: "50–74", c: "#84CC16" },
  { key: "bad", range: "25–49", c: "#F97316" },
  { key: "awful", range: "0–24", c: "#DC2626" },
];
const LANGS = [{ c: "ru", n: "Русский" }, { c: "en", n: "English" }, { c: "kk", n: "Қазақша" }];

export default function Profile() {
  const { user, logout, setUserPrefs } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(getLocalPrefs());
  const [allergyText, setAllergyText] = useState("");
  const [openHelp, setOpenHelp] = useState(null);

  useEffect(() => {
    const base = user?.preferences && Object.keys(user.preferences).length
      ? { ...getLocalPrefs(), ...user.preferences } : getLocalPrefs();
    setPrefs(base);
    setAllergyText((base.allergies || []).join(", "));
  }, [user]);

  const toggleDiet = (d) => {
    setPrefs((p) => ({ ...p, diets: p.diets.includes(d) ? p.diets.filter((x) => x !== d) : [...p.diets, d] }));
  };

  const persist = async () => {
    const allergies = allergyText.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      const merged = await savePrefs({ ...prefs, allergies, language: lang }, !!user);
      if (user) setUserPrefs(merged);
      setPrefs(merged);
      toast.success(t("me.saved"));
    } catch {
      toast.error(t("product.error"));
    }
  };

  const shareApp = async () => {
    const text = `${t("about.shareText")} ${window.location.origin}`;
    if (navigator.share) { try { await navigator.share({ title: "NutriScan", text, url: window.location.origin }); return; } catch { /* noop */ } }
    try { await navigator.clipboard.writeText(text); toast.success(t("share.copied")); } catch { /* noop */ }
  };

  const HELP = [
    { icon: Target, key: "score" },
    { icon: Layers, key: "nova" },
    { icon: FlaskConical, key: "add" },
  ];

  return (
    <AppShell>
      <h1 className="font-heading text-2xl font-extrabold mt-2 mb-4" data-testid="profile-title">{t("me.title")}</h1>

      <div className="bg-[#FBF8F2] rounded-2xl border border-black/5 p-4 flex items-center gap-3 mb-6" data-testid="profile-account">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <User className="w-6 h-6 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-800 truncate">{user ? user.name || user.email : t("me.anon")}</p>
          <p className="text-sm text-slate-400 truncate">{user ? user.email : t("me.anonDesc")}</p>
        </div>
        {user ? (
          <Button variant="ghost" size="sm" onClick={logout} data-testid="profile-logout" className="rounded-full text-slate-500">
            <LogOut className="w-4 h-4" />
          </Button>
        ) : (
          <Button size="sm" onClick={() => navigate("/auth")} data-testid="profile-login" className="rounded-full bg-primary">
            {t("me.loginBtn")}
          </Button>
        )}
      </div>

      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> {t("me.language")}</p>
      <div className="flex gap-2 mb-6">
        {LANGS.map((l) => (
          <button key={l.c} onClick={() => setLang(l.c)} data-testid={`lang-${l.c}`}
            className={`flex-1 h-10 rounded-xl text-sm font-semibold border transition-colors ${lang === l.c ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}>
            {l.n}
          </button>
        ))}
      </div>

      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("me.myFilters")}</p>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="profile-diets">
        {DIETS.map((d) => {
          const on = prefs.diets.includes(d);
          return (
            <button key={d} onClick={() => toggleDiet(d)} data-testid={`diet-${d}`}
              className={`px-3.5 h-9 rounded-full text-sm font-medium border transition-colors flex items-center gap-1.5 ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}>
              {on && <Check className="w-3.5 h-3.5" />}{t(`diet.${d}`)}
            </button>
          );
        })}
      </div>
      <Input value={allergyText} onChange={(e) => setAllergyText(e.target.value)} data-testid="profile-allergies"
        placeholder={t("me.allergiesPh")} className="rounded-xl mb-3" />
      {!user && <p className="text-xs text-amber-600 mb-3">{t("me.loginPrompt")}</p>}
      <Button onClick={persist} data-testid="profile-save" className="w-full h-11 rounded-xl bg-primary mb-8">{t("me.save")}</Button>

      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("me.help")}</p>
      <div className="space-y-2 mb-4">
        {HELP.map(({ icon: Icon, key }) => (
          <div key={key} className="bg-[#FBF8F2] rounded-2xl border border-black/5 overflow-hidden">
            <button onClick={() => setOpenHelp(openHelp === key ? null : key)} data-testid={`help-${key}`}
              className="w-full flex items-center gap-3 p-4 text-left">
              <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="flex-1 font-semibold text-slate-800">{t(`help.${key}T`)}</span>
              <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${openHelp === key ? "rotate-180" : ""}`} />
            </button>
            {openHelp === key && key !== "add" && key !== "nova" && key !== "score" && <p className="px-4 pb-4 text-sm text-slate-500 leading-relaxed">{t(`help.${key}S`)}</p>}
            {openHelp === key && key === "score" && (
              <div className="px-4 pb-4 space-y-3" data-testid="score-explainer">
                <p className="text-sm text-slate-500 leading-relaxed">{t("help.scoreIntro")}</p>
                {SCORE_COMPONENTS.map((c) => (
                  <div key={c} className="rounded-xl bg-slate-50 p-3">
                    <p className="font-semibold text-slate-800 text-sm mb-0.5">{t(`help.score${c}T`)}</p>
                    <p className="text-sm text-slate-500 leading-snug">{t(`help.score${c}D`)}</p>
                  </div>
                ))}
                <p className="text-xs text-slate-400 leading-snug">{t("help.scoreNote")}</p>
                <p className="font-semibold text-slate-700 text-sm pt-1">{t("help.gradesTitle")}</p>
                <div className="space-y-1.5">
                  {GRADE_TIERS.map((tier) => (
                    <div key={tier.key} className="flex items-center gap-2.5">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: tier.c }} />
                      <span className="text-sm font-semibold text-slate-700 w-16 shrink-0">{tier.range}</span>
                      <span className="text-sm text-slate-500">{t(`grade.${tier.key}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {openHelp === key && key === "nova" && (
              <div className="px-4 pb-4 space-y-2.5" data-testid="nova-scale">
                <p className="text-sm text-slate-500 leading-relaxed mb-1">{t("help.novaS")}</p>
                {NOVA_GROUPS.map((n) => (
                  <div key={n.g} className="flex gap-3">
                    <span className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-bold text-white shrink-0" style={{ background: n.c }}>{n.g}</span>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{t(`nova.n${n.g}`)}</p>
                      <p className="text-sm text-slate-500 leading-snug">{t(`help.novaD${n.g}`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {openHelp === key && key === "add" && (
              <div className="px-4 pb-4 space-y-2.5" data-testid="risk-scale">
                {[
                  { c: "#16A34A", T: "risk.safeT", D: "risk.safeD" },
                  { c: "#84CC16", T: "risk.limT", D: "risk.limD" },
                  { c: "#F59E0B", T: "risk.modT", D: "risk.modD" },
                  { c: "#DC2626", T: "risk.dangT", D: "risk.dangD" },
                ].map((r) => (
                  <div key={r.T} className="flex gap-3">
                    <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: r.c }} />
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{t(r.T)}</p>
                      <p className="text-sm text-slate-500 leading-snug">{t(r.D)}</p>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-slate-400 pt-1">{t("risk.src")}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("about.title")}</p>
      <div className="bg-[#FBF8F2] rounded-2xl border border-black/5 divide-y divide-black/5 mb-4" data-testid="about-block">
        <a href={`mailto:${t("about.supportEmail")}`} data-testid="about-report" className="w-full flex items-center gap-3 p-4">
          <span className="w-5 h-5 shrink-0 text-amber-500">⚠</span>
          <span className="flex-1"><span className="font-semibold text-slate-800 block">{t("about.report")}</span><span className="text-xs text-slate-400">{t("about.supportEmail")}</span></span>
          <span className="text-slate-300">›</span>
        </a>
        <button onClick={shareApp} data-testid="about-share" className="w-full flex items-center gap-3 p-4 text-left">
          <span className="w-5 h-5 shrink-0 text-emerald-500">↗</span>
          <span className="flex-1 font-semibold text-slate-800">{t("about.shareApp")}</span>
          <span className="text-slate-300">›</span>
        </button>
        <div className="w-full flex items-center gap-3 p-4">
          <span className="w-5 h-5 shrink-0 text-slate-400">ⓘ</span>
          <span className="flex-1 font-semibold text-slate-800">{t("about.version")}</span>
          <span className="text-sm text-slate-400" data-testid="about-version">{t("about.versionNum")}</span>
        </div>
      </div>
      <p className="text-center text-xs text-slate-300 mb-4">NutriScan · 2026</p>
    </AppShell>
  );
}
